import { useState, useEffect } from 'react';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import { 
  DocumentDuplicateIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Header from './Home/components/Header';
import { Link } from "@tanstack/react-router";
import { useSEO } from '../hooks/useSEO';

const CompressionOptionsDoc = () => (
  <div className="mt-4 border border-border rounded-lg overflow-hidden">
    <div className="p-4 bg-background-soft border-b border-border">
      <p className="text-muted-foreground mb-4">
        Configure compression settings via the <code className="text-accent">x-compression-options</code> header. 
        Send a JSON object with the following structure. Each tab below represents a configuration section:
      </p>
      <SyntaxHighlighter 
        language="json" 
        style={dracula}
        className="rounded-lg p-4 text-sm"
      >
        {`{
  "texture": { /* Texture compression */ },
  "draco": { /* Geometry compression */ },
  "transforms": { /* Mesh optimizations */ }
}`}
      </SyntaxHighlighter>
    </div>

    <TabGroup>
      <TabList className="flex space-x-1 bg-background-elevated p-2">
        {['texture', 'draco', 'transforms'].map((category) => (
          <Tab
            key={category}
            className={({ selected }) =>
              `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                selected
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-background-soft'
              }`
            }
          >
            {category}
          </Tab>
        ))}
      </TabList>

      <TabPanels className="p-4 bg-background-soft">
        {/* Texture Tab */}
        <TabPanel className="space-y-4">
          <OptionItem
            name="enabled"
            type="boolean"
            default="true"
            description="Enable texture compression. Reduces texture memory usage by 4-8x. Essential for WebGL apps targeting mobile devices."
          />
          <OptionItem
            name="format"
            type="string ('jpeg' | 'png' | 'webp' | 'avif')"
            default="'webp'"
            description="Output format for textures. WebP/AVIF provide better compression than PNG/JPEG but may have compatibility considerations."
          />
          <OptionItem
            name="resize"
            type="number[2]"
            default="[1024, 1024]"
            description="Maximum texture dimensions [width, height]. Reduce to save memory, increase for high-detail assets."
          />
          <OptionItem
            name="quality"
            type="number (1-100)"
            default="80"
            description="Quality setting for lossy formats. Lower values reduce file size but increase compression artifacts. 75-85 recommended for WebP/AVIF."
          />
        </TabPanel>

        {/* Draco Tab */}
        <TabPanel className="space-y-4">
          <OptionItem
            name="enabled"
            type="boolean"
            default="true"
            description="Enable Draco mesh compression. Reduces geometry size by 60-90%. Requires Draco decoder on client side."
          />
          <OptionItem
            name="compressionLevel"
            type="number (0-10)"
            default="7"
            description="Compression effort vs speed tradeoff. Higher values produce smaller files but take longer to encode."
          />
          <OptionItem
            name="quantizationBits"
            type="number (1-16)"
            default="14"
            description="Precision reduction for vertex attributes. Lower values save space but may cause rendering artifacts."
          />
        </TabPanel>

        {/* Transforms Tab */}
        <TabPanel className="space-y-4">
          <OptionItem
            name="deduplicate"
            type="boolean"
            default="true"
            description="Remove duplicate materials/textures/meshes. Safe optimization with no visual impact."
          />
          <OptionItem
            name="removeUnused"
            type="boolean"
            default="true"
            description="Strip unused vertices, materials, and textures. Reduces file size with no quality loss."
          />
          <OptionItem
            name="instance"
            type="boolean"
            default="true"
            description="Create instanced copies of repeated meshes. Reduces geometry duplication in scenes with repeated objects."
          />
          <OptionItem
            name="flatten"
            type="boolean"
            default="true"
            description="Merge transform hierarchies. Improves rendering performance but may break animation rigs."
          />
          <OptionItem
            name="merge"
            type="boolean"
            default="true"
            description="Combine compatible meshes. Reduces draw calls but may increase vertex count."
          />
          <OptionItem
            name="weld"
            type="boolean"
            default="true"
            description="Merge overlapping vertices. Reduces vertex count with no visual change."
          />
          <OptionItem
            name="simplify"
            type="number (0-1)"
            default="0.0"
            description="Aggressive mesh simplification ratio. 0=disabled, 1=maximum reduction. Use 0.5-0.7 for visible reduction. Handle organic shapes with care."
          />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </div>
);

const OptionItem = ({ name, type, default: defaultValue, description }) => (
  <div className="flex gap-4">
    <div className="w-1/3 font-mono">
      <span className="text-accent">{name}</span>
      <span className="text-muted ml-2 text-sm">({type})</span>
    </div>
    <div className="flex-1">
      <p className="text-muted-foreground">{description}</p>
      {defaultValue && (
        <p className="text-sm text-muted mt-1">
          Default: <span className="font-mono">{defaultValue}</span>
        </p>
      )}
    </div>
  </div>
);

const CodeExample = ({ language, children }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <SyntaxHighlighter 
        language={language}
        style={dracula}
        className="rounded-lg p-4 text-sm border border-border"
      >
        {children}
      </SyntaxHighlighter>
      <button
        onClick={copyToClipboard}
        className="absolute top-3 right-3 p-1.5 text-muted hover:text-accent transition-colors"
      >
        {copied ? (
          <CheckIcon className="w-4 h-4" />
        ) : (
          <DocumentDuplicateIcon className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};

const EndpointSection = ({ 
  method, 
  path, 
  description, 
  headers, 
  parameters, 
  responseExample, 
  errors, 
  id,
  isActive,
  setActiveEndpoint
}) => {
  const [open, setOpen] = useState(isActive || false);
  const isUploadEndpoint = path.includes('/upload/');
  
  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive]);

  const toggleOpen = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    
    if (newOpenState) {
      setActiveEndpoint(id);
      window.history.pushState(null, null, `#${id}`);
    }
  };

  const codeExamples = {
    curl: (() => {
      const parts = [
        `curl -X ${method} \\`,
        `  -H "Authorization: <TOKEN>" \\`,
        isUploadEndpoint && `  -H "x-compression-options: {\\"draco\\":{\\"compressionLevel\\":9}}" \\`,
        isUploadEndpoint && `  -F "file=@model.glb" \\`,
        `  https://api.compress-glb.com${path}${parameters.length ? '?callback_url=https://your.app/callback' : ''}`
      ].filter(Boolean);
      return parts.join('\n');
    })(),
    
    javascript: (() => {
      const headerLines = [
        `'Authorization': '<TOKEN>'`,
        isUploadEndpoint && `'x-compression-options': JSON.stringify({ draco: { enabled: true, compressionLevel: 9 } })`
      ].filter(Boolean);
      const bodyLine = isUploadEndpoint 
        ? `body: (() => {
            const formData = new FormData();
            formData.append('file', fileInput.files[0]);
            return formData;
          })()`
        : 'null';
      return `fetch('https://api.compress-glb.com${path}', {
  method: '${method}',
  headers: {
    ${headerLines.join(',\n    ')}
  },
  ${bodyLine}
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`;
    })(),

    python: (() => {
      const headerLines = [
        `"Authorization": "<TOKEN>"`,
        isUploadEndpoint && `"x-compression-options": json.dumps({ draco: { enabled: True, compressionLevel: 9 } })`
      ].filter(Boolean);
      const requestParams = parameters.length ? `{'callback_url': 'https://your.app/callback'}` : 'None';
      const requestCode = isUploadEndpoint 
        ? `with open('model.glb', 'rb') as f:
    files = {'file': f}
    response = requests.${method.toLowerCase()}(
        url, 
        headers=headers, 
        files=files, 
        params=${requestParams}
    )`
        : `response = requests.${method.toLowerCase()}(url, headers=headers${parameters.length ? ', params=' + requestParams : ''})`;
      return `import requests

url = "https://api.compress-glb.com${path}"
headers = {
    ${headerLines.join(',\n    ')}
}

${requestCode}

print(response.json())`;
    })(),

    go: (() => {
      const bodyCode = isUploadEndpoint ? [
        `file, _ := os.Open("model.glb")`,
        `part, _ := writer.CreateFormFile("file", "model.glb")`,
        `io.Copy(part, file)`,
        `writer.Close()`
      ] : [];
      return `package main

import (
  "bytes"
  "fmt"
  "mime/multipart"
  "net/http"
  "os"
)

func main() {
  body := &bytes.Buffer{}
  writer := multipart.NewWriter(body)
  ${bodyCode.join('\n  ')}
  
  req, _ := http.NewRequest("${method}", 
    "https://api.compress-glb.com${path}", 
    body)
  
  req.Header.Set("Authorization", "<TOKEN>")
  ${isUploadEndpoint ? `req.Header.Set("x-compression-options", \`{"draco":{"enabled":true,"compressionLevel":9}}\`)` : ''}
  
  client := &http.Client{}
  resp, err := client.Do(req)
  if err != nil {
    panic(err)
  }
  defer resp.Body.Close()
}`;
    })(),

    csharp: (() => {
      const uploadCode = isUploadEndpoint ? [
        `var content = new MultipartFormDataContent();`,
        `var fileContent = new ByteArrayContent(File.ReadAllBytes("model.glb"));`,
        `content.Add(fileContent, "file", "model.glb");`,
        ``,
        `client.DefaultRequestHeaders.Add("x-compression-options", "{\\"draco\\":{\\"enabled\\":true,\\"compressionLevel\\":9}}");`
      ] : [];
      return `using System;
using System.Net.Http;
using System.IO;
using System.Threading.Tasks;

class Program {
  static async Task Main() {
    using var client = new HttpClient();
    client.DefaultRequestHeaders.Authorization = 
      new System.Net.Http.Headers.AuthenticationHeaderValue("Bearer", "<TOKEN>");
    ${uploadCode.join('\n    ')}
    var response = await client.${method.toLowerCase()}Async(
      "https://api.compress-glb.com${path}", 
      ${isUploadEndpoint ? 'content' : 'null'});
    
    var result = await response.Content.ReadAsStringAsync();
    Console.WriteLine(result);
  }
}`;
    })()
  };

  return (
    <div id={id} className="bg-background-soft rounded-lg p-6 mb-8 scroll-mt-20">
      <button
        onClick={toggleOpen}
        className="w-full flex justify-between items-center"
      >
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded ${method === 'POST' ? 'bg-primary text-primary-foreground' : 'bg-background-elevated'}`}>
            {method}
          </span>
          <span className="font-mono text-lg">{path}</span>
        </div>
        <ChevronDownIcon className={`w-6 h-6 transform transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-6 space-y-6">
          <p className="text-muted-foreground">{description}</p>

          {headers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-2 text-foreground">Headers</h3>
              <div className="grid gap-2">
                {headers.map((header) => (
                  <div key={header.name} className="flex gap-4 font-mono">
                    <span className="text-accent">{header.name}</span>
                    <span className="text-muted">{header.required ? 'required' : 'optional'}</span>
                    <span className="text-muted-foreground">{header.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {parameters.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-2 text-foreground">Query Parameters</h3>
              <div className="grid gap-2">
                {parameters.map((param) => (
                  <div key={param.name} className="flex gap-4 font-mono">
                    <span className="text-accent">{param.name}</span>
                    <span className="text-muted">{param.required ? 'required' : 'optional'}</span>
                    <span className="text-muted-foreground">{param.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold mb-4 text-foreground">Example Request</h3>
            <TabGroup>
              <TabList className="flex space-x-1 bg-background-elevated p-2 mb-4">
                {['curl', 'javascript', 'python', 'go', 'c#'].map((lang) => (
                  <Tab
                    key={lang}
                    className={({ selected }) =>
                      `px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        selected
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-background-soft'
                      }`
                    }
                  >
                    {lang}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {Object.entries(codeExamples).map(([lang, code]) => (
                  <TabPanel key={lang}>
                    <CodeExample language={lang === 'curl' ? 'bash' : lang === 'csharp' ? 'csharp' : lang}>
                      {code}
                    </CodeExample>
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2 text-foreground">Example Response</h3>
            <CodeExample language="json">
              {JSON.stringify(responseExample, null, 2)}
            </CodeExample>
          </div>

          {errors.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-2 text-foreground">Errors</h3>
              <div className="grid gap-2">
                {errors.map((error) => (
                  <div key={error.code} className="flex gap-4">
                    <span className="text-danger-muted w-12">{error.code}</span>
                    <span className="text-muted-foreground">{error.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {method === 'POST' && path.includes('/upload/') && <CompressionOptionsDoc />}
        </div>
      )}
    </div>
  );
};

const DocsPage = () => {
  const [activeEndpoint, setActiveEndpoint] = useState(null);

  useSEO({
    title: 'API Documentation - Compress GLB | Complete Integration Guide',
    description: 'Complete API reference for Compress GLB including endpoints, authentication, code examples, and integration guides for 3D model compression.',
    keywords: 'Compress GLB API, 3D compression API, GLB API documentation, GLTF compression API, REST API',
    canonical: 'https://compress-glb.com/docs/api',
    ogImage: '/images/og/docs.png',
    ogTitle: 'Compress GLB API Documentation',
    ogDescription: 'Complete API reference and integration guide for 3D model compression.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      "name": "Compress GLB API Documentation",
      "description": "Complete API reference for 3D model compression service",
      "url": "https://compress-glb.com/docs/api"
    }
  });
  
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setActiveEndpoint(hash);
      
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleEndpointClick = (endpointId) => {
    setActiveEndpoint(endpointId);
    
    window.history.pushState(null, null, `#${endpointId}`);
    
    const element = document.getElementById(endpointId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="flex flex-col items-center">
        <main className="max-w-7xl w-full px-4 sm:px-8 lg:px-16 flex-1 flex gap-8">
          {/* Sticky Sidebar */}
          <div className="w-80 shrink-0 hidden md:block sticky top-20 h-[calc(100vh-90px)] overflow-y-auto">
            <nav className="space-y-4 pr-4 pt-8">
              <div className="bg-primary/10 p-4 rounded-lg">
                <h3 className="text-sm font-semibold mb-2 text-primary">Base URL</h3>
                <p className="font-mono text-xs text-muted-foreground break-all">
                  https://api.compress-glb.com/
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-muted px-2">Endpoints</h4>
                <div className="space-y-1">
                  <button 
                    onClick={() => handleEndpointClick('sync-upload')}
                    className={`block w-full text-left px-2 py-1.5 text-sm rounded hover:bg-background-elevated ${activeEndpoint === 'sync-upload' ? 'bg-background-elevated text-foreground' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
                  >
                    POST /upload/sync
                  </button>
                  <button 
                    onClick={() => handleEndpointClick('async-upload')}
                    className={`block w-full text-left px-2 py-1.5 text-sm rounded hover:bg-background-elevated ${activeEndpoint === 'async-upload' ? 'bg-background-elevated text-foreground' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
                  >
                    POST /upload/async
                  </button>
                  <button 
                    onClick={() => handleEndpointClick('poll-status')}
                    className={`block w-full text-left px-2 py-1.5 text-sm rounded hover:bg-background-elevated ${activeEndpoint === 'poll-status' ? 'bg-background-elevated text-foreground' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
                  >
                    GET /poll/{'<job_id>'}
                  </button>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-semibold text-muted px-2">Resources</h4>
                <div className="space-y-1 mt-2">
                  <Link
                    to="/app/dashboard" 
                    className="block px-2 py-1.5 text-sm rounded hover:bg-background-elevated text-accent hover:text-accent-muted transition-colors"
                  >
                    API dashboard
                  </Link>
                  <Link
                    to="/app/billing" 
                    className="block px-2 py-1.5 text-sm rounded hover:bg-background-elevated text-accent hover:text-accent-muted transition-colors"
                  >
                    Billing
                  </Link>
                </div>
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="py-8 h-[calc(100vh-90px)] overflow-y-auto">
            <div className="space-y-8">
              <h1 className="text-4xl font-bold text-accent">API docs</h1>
              
              <div className="bg-background-elevated p-6 rounded-lg">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2 text-primary">Getting Started</h3>
                  <p className="text-muted-foreground">
                    Every successful call to the upload API consumes 1 credit. Your current credit 
                    balance is visible in your account dashboard. You can check your credit balance 
                    programmatically using the X-Credits-Remaining header in the response of every upload request.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-background-soft p-4 rounded-lg border border-border">
                    <h4 className="text-sm font-semibold mb-2 text-primary">Authentication</h4>
                    <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                      <li>Log in to your <Link to="/app/dashboard" className="underline text-accent">account dashboard</Link></li>
                      <li>Navigate to API Keys</li>
                      <li>Enter a name for the key and click on "Create key"</li>
                      <li>Copy the token and store it securely</li>
                    </ol>
                  </div>

                  <div className="bg-background-soft p-4 rounded-lg border border-border">
                    <h4 className="text-sm font-semibold mb-2 text-primary">Rate Limits</h4>
                    <ul className="text-muted-foreground text-sm space-y-2">
                      <li>• 25 requests/minute per token</li>
                      <li>• max. 10 files per request</li>
                      <li>• max. 500MB per file</li>
                    </ul>
                  </div>
                </div>
              </div>

              <EndpointSection
                id="sync-upload"
                method="POST"
                path="/api/1/files/upload/sync"
                description="Synchronous file upload and compression. Returns compressed files immediately."
                headers={[
                  { 
                    name: 'Authorization', 
                    required: true, 
                    description: 'token (e.g. sk_live_12345)' 
                  },
                  { 
                    name: 'x-compression-options', 
                    required: false, 
                    description: 'JSON configuration object for compression settings' 
                  }
                ]}
                parameters={[]}
                responseExample={{
                  id: "6eb31eb6-d226-4317-9ff4-486529aa3366",
                  compressed_file_size: 100,
                  compressed_file_url: "https://files.compress-glb.com/...",
                  compression_ratio: 0.90,
                  file_name: "compressed.glb",
                  original_file_size: 1000,
                  original_file_url: "https://files.compress-glb.com/...",
                  status: "SUCCESS"
                }}
                errors={[
                  { code: 400, message: "No files provided" },
                  { code: 401, message: "Invalid authentication" },
                  { code: 402, message: "Insufficient credits" },
                  { code: 413, message: "File too large" },
                  { code: 429, message: "Rate limit exceeded" },
                ]}
                isActive={activeEndpoint === 'sync-upload'}
                setActiveEndpoint={setActiveEndpoint}
              />

              <EndpointSection
                id="async-upload"
                method="POST"
                path="/api/1/files/upload/async"
                description="Asynchronous file upload with callback support. Returns job IDs for polling."
                headers={[
                  { 
                    name: 'Authorization', 
                    required: true, 
                    description: 'token (e.g. sk_live_12345)' 
                  },
                  { 
                    name: 'x-compression-options', 
                    required: false, 
                    description: 'JSON configuration object for compression settings' 
                  }
                ]}
                parameters={[
                  { 
                    name: 'callback_url', 
                    required: false, 
                    description: 'HTTPS URL to receive POST request with processing results. If not provided, use the poll endpoint to check status.' 
                  }
                ]}
                responseExample={[{
                  id: "job_123",
                  poll_url: "/api/1/files/poll/job_123"
                }]}
                errors={[
                  { code: 400, message: "No files provided" },
                  { code: 401, message: "Invalid authentication" },
                  { code: 402, message: "Insufficient credits" },
                  { code: 413, message: "File too large" },
                  { code: 429, message: "Rate limit exceeded" },
                ]}
                isActive={activeEndpoint === 'async-upload'}
                setActiveEndpoint={setActiveEndpoint}
              />

              <EndpointSection
                id="poll-status"
                method="GET"
                path="/api/1/files/poll/<job_id>"
                description="Poll status of an asynchronous compression job"
                headers={[
                  { 
                    name: 'Authorization', 
                    required: true, 
                    description: 'token (e.g. sk_live_12345)' 
                  }
                ]}
                parameters={[]}
                responseExample={{
                  id: "job_123",
                  status: "PROCESSING",
                  original_file_url: "https://files.compress-glb.com/..."
                }}
                errors={[
                  { code: 404, message: "Job not found" },
                  { code: 401, message: "Invalid authentication" }
                ]}
                isActive={activeEndpoint === 'poll-status'}
                setActiveEndpoint={setActiveEndpoint}
              />
            </div>
          </div>
        </main>
      </div> 
    </div>
  );
};

export default DocsPage;
